import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Send, Paperclip, X, Check, Clock, AlertCircle, Loader2,
    MessageSquare, MessagesSquare, CornerUpLeft, Tag, RotateCcw, Plus, ChevronDown,
} from "lucide-react";
import AppShell, { ACCENT, ACCENT_DEEP, SHELL_BG } from "../components/AppShell";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { getAuthTokens } from "@/utils/auth";
import { BRAND_SERVICE_VIEW } from "@/brand";

// ────────────────────────────────────────────────────────────────────────────
// Message Us.
//
// A guided intake that behaves like a chat: the assistant asks what the message
// is about, you answer by tapping, it narrows down once, and then it hands you
// the composer. Two taps and the team receives the message already labelled,
// instead of a blank Subject field nobody fills in well.
//
// What the assistant is, exactly — the line matters, because getting it wrong
// makes the product lie to a paying client:
//
//   It ROUTES. It does not ANSWER. Every one of its lines is scripted, and none
//   of them claims to know anything about your file. It asks, it confirms, it
//   says a person will come back to you. So there is no case where a client
//   reads an answer here and acts on it — and when the answer does come it is
//   visibly from a person, in its own bubble, alongside the notification and the
//   email. Nobody is left watching for a reply that only exists elsewhere.
//
// The guided turns are ephemeral: they shape the message, they are not stored.
// What gets persisted is what it always was — one message, with the picks
// folded into its topic so the team can scan the queue.
//
// The flow is skipped for anyone who already has a thread, or who arrived by
// replying to a notification. Asking "what is this about?" above six months of
// history is worse than asking nothing.
//
// Both sides live in one store, users/{uid}/messages: what the client sends
// (POST /auth/user-message) and what the team replies (written by the admin
// panel through POST /auth/message-reply). The pane reads the lot from
// GET /auth/user-messages and polls it, so a reply appears while the page is
// open rather than only in Notifications.
//
// `conversationId` splits that store into conversations. "New conversation"
// mints one client-side and runs the intake again; nothing is written until the
// first message is sent, so an abandoned one leaves no trace. Messages stored
// before conversations existed carry no id and group as one first thread.
// ────────────────────────────────────────────────────────────────────────────

const IS_CONSULTING = BRAND_SERVICE_VIEW === "consulting";

/* ── The script ──────────────────────────────────────────────────────────────
   One question, one narrowing, then the composer. Deeper than that and it stops
   feeling like a conversation and starts feeling like a phone menu.

   `ask` is what the assistant says after the topic is picked; `options` are the
   chips under it. A topic with no options goes straight to the composer, which
   is what "Something else" needs — the whole point of that chip is that the
   person does not want to be triaged. */
type Topic = { label: string; ask?: string; options?: string[] };

const TOPICS: Topic[] = IS_CONSULTING
    ? [
        { label: "My raise", ask: "Where are you in it?", options: ["Getting ready", "In conversations", "At term sheet", "Just exploring"] },
        { label: "Sprints and experts", ask: "What do you need?", options: ["Scoping a sprint", "About an expert", "A sprint in progress"] },
        { label: "Documents and data room", ask: "What are we looking at?", options: ["Uploading something", "Finding a document", "Access for someone else"] },
        { label: "Billing", ask: "What is it about?", options: ["An invoice", "A payment", "My plan"] },
        { label: "Something else" },
    ]
    : [
        { label: "VAT and tax filing", ask: "Which part of it?", options: ["VAT registration", "A return to file", "Corporate Tax", "A penalty or FTA notice"] },
        { label: "Trade licence and visas", ask: "What do you need?", options: ["A new licence", "A renewal", "A visa application", "An amendment"] },
        { label: "Documents and data room", ask: "What are we looking at?", options: ["Uploading something", "Finding a document", "Access for someone else"] },
        { label: "Billing", ask: "What is it about?", options: ["An invoice", "A payment", "My plan"] },
        { label: "Something else" },
    ];

/** Closing line — the handover to the composer. Never promises an answer here. */
const HANDOVER = "Got it. Tell me the details below and attach anything that helps \u2014 I will put this in front of the right person on the team.";

/** One turn of the guided exchange. Ephemeral: shapes the message, never stored. */
type Turn = { from: "bot" | "me"; text: string };

type Attachment = { name: string; url: string; type: string; size: number };

type ThreadMessage = {
    id: string;
    /** Which side of the thread. Entries stored before replies landed here have
     *  no `from` and are read as the client's. */
    from: "client" | "admin";
    conversationId: string;
    subject: string;
    message: string;
    attachments: Attachment[];
    replyToTitle?: string | null;
    adminName?: string;
    sentAt: string;
    /** Only set on messages sent this session — server history has no status. */
    status?: "sending" | "sent" | "failed";
};

/** The bucket for messages stored before conversations existed. */
const LEGACY_CONV = "first";

const newConvId = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** A conversation's name in the switcher: the topic it opened with, which is
 *  what the intake was collecting in the first place. */
const convTitle = (msgs: ThreadMessage[]) => {
    const opener = msgs.find(m => m.from === "client") || msgs[0];
    const topic = (opener?.subject || "").trim();
    if (topic) return topic;
    const first = ((opener?.message || "").split("\n")[0] || "").trim();
    if (!first) return "Conversation";
    return first.length > 40 ? `${first.slice(0, 37).trimEnd()}\u2026` : first;
};

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const fmtClock = (iso: string) => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return "";
    return new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const dayKey = (iso: string) => {
    const t = Date.parse(iso);
    return Number.isNaN(t) ? "" : new Date(t).toDateString();
};

const dayLabel = (iso: string) => {
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return "";
    const d = new Date(t);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86400000);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const fmtSize = (bytes: number) =>
    bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** Show the topic above the text only when it is not just the opening words of
 *  the message — a topic derived from the body would otherwise read twice. */
const topicWorthShowing = (m: ThreadMessage) => {
    const s = (m.subject || "").replace(/\u2026$/, "").trim();
    if (!s) return false;
    const body = stripHtml(m.message).toLowerCase();
    return !body.startsWith(s.toLowerCase().slice(0, Math.min(24, s.length)));
};

/* ── Thread pieces ──────────────────────────────────────────────────────── */

const Avatar = ({ size = 32 }: { size?: number }) => (
    <span
        className="rounded-full grid place-items-center text-white font-black shrink-0 leading-none"
        style={{ width: size, height: size, background: ACCENT_DEEP, fontSize: Math.round(size * 0.42) }}
        aria-hidden="true"
    >
        C
    </span>
);

const DayChip = ({ iso }: { iso: string }) => (
    <div className="flex justify-center py-1.5">
        <span className="rounded-full bg-white/80 border border-slate-200 px-3 py-1 text-[10.5px] font-black uppercase tracking-widest text-gray-400">
            {dayLabel(iso)}
        </span>
    </div>
);

const SystemLine = ({ children }: { children: React.ReactNode }) => (
    <p className="text-center text-[11.5px] text-gray-400 font-medium px-6 py-1.5 leading-relaxed">{children}</p>
);

/** A note from us. Fixed copy, shown once at the top — never a reply. */
const TeamBubble = ({ children, withAvatar }: { children: React.ReactNode; withAvatar: boolean }) => (
    <div className="mc-b flex items-end gap-2">
        {withAvatar ? <Avatar /> : <span className="w-8 shrink-0" aria-hidden="true" />}
        <div className="max-w-[86%] sm:max-w-[74%] rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm">
            <p className="text-[13.5px] leading-relaxed text-gray-700">{children}</p>
        </div>
    </div>
);

/** Files on a message. `onAccent` picks the palette: light-on-accent inside my
 *  own bubbles, dark-on-white inside the team's. */
const AttachmentList = ({ items, onAccent, failed }: { items: Attachment[]; onAccent: boolean; failed?: boolean }) => {
    if (!items.length) return null;
    const borderColor = failed ? "#FECACA" : onAccent ? "rgba(255,255,255,.3)" : "#E2E8F0";
    const chipBg = failed ? "#FEE2E2" : onAccent ? "rgba(255,255,255,.16)" : "#F1F5F9";
    return (
        <div className="mt-2 space-y-1.5">
            {items.map((att, i) => {
                const isImg = (att.type || "").startsWith("image/");
                const isData = (att.url || "").startsWith("data:");
                const link = isData
                    ? { download: att.name }
                    : { target: "_blank", rel: "noopener noreferrer" };
                return isImg ? (
                    <a key={i} href={att.url} {...link} className="block">
                        <img src={att.url} alt={att.name} className="rounded-xl max-h-44 w-auto border" style={{ borderColor }} />
                    </a>
                ) : (
                    <a
                        key={i}
                        href={att.url}
                        {...link}
                        className="flex items-center gap-2 rounded-xl px-2.5 py-2 hover:opacity-90 transition"
                        style={{ background: chipBg }}
                    >
                        <Paperclip className="w-3.5 h-3.5 shrink-0" />
                        <span className="text-[12px] font-semibold truncate flex-1">{att.name}</span>
                        <span className="text-[10.5px] opacity-75 shrink-0">{fmtSize(att.size)}</span>
                    </a>
                );
            })}
        </div>
    );
};

/* The team's reply. Same store as my own messages, opposite side of the thread
   — and deliberately not styled like the assistant's scripted lines, because
   this one is a person answering. */
const AdminBubble = ({ m }: { m: ThreadMessage }) => (
    <div className="mc-b flex items-end gap-2">
        <Avatar />
        <div className="max-w-[86%] sm:max-w-[74%]">
            <div className="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm">
                <span className="block text-[10.5px] font-black uppercase tracking-widest text-gray-400 mb-1">
                    {m.adminName || "Conncct Team"}
                </span>
                <p className="text-[13.5px] leading-relaxed text-gray-700 whitespace-pre-line break-words">{m.message}</p>
                <AttachmentList items={m.attachments} onAccent={false} />
            </div>
            <span className="block mt-1 ms-1 text-[10.5px] font-semibold text-gray-400">{fmtClock(m.sentAt)}</span>
        </div>
    </div>
);

/* One of my messages — module scope, so a keystroke in the composer
   re-renders the page without remounting (and re-animating) the thread. */
const MyBubble = ({ m, onRetry }: { m: ThreadMessage; onRetry: (m: ThreadMessage) => void }) => {
    const failed = m.status === "failed";
    return (
        <div className="mc-b flex flex-col items-end">
            <div
                className="max-w-[88%] sm:max-w-[76%] rounded-2xl rounded-br-md px-3.5 py-2.5 shadow-sm"
                style={{
                    background: failed ? "#FEF2F2" : ACCENT,
                    border: failed ? "1px solid #FECACA" : "1px solid transparent",
                    color: failed ? "#991B1B" : "#fff",
                }}
            >
                {m.replyToTitle && (
                    <span
                        className="flex items-center gap-1.5 text-[11px] font-bold mb-1.5 pb-1.5 border-b truncate"
                        style={{
                            color: failed ? "#B91C1C" : "rgba(255,255,255,.8)",
                            borderColor: failed ? "#FECACA" : "rgba(255,255,255,.22)",
                        }}
                    >
                        <CornerUpLeft className="w-3 h-3 shrink-0" />
                        {m.replyToTitle}
                    </span>
                )}
                {topicWorthShowing(m) && (
                    <span
                        className="block text-[10.5px] font-black uppercase tracking-widest mb-1.5"
                        style={{ color: failed ? "#B91C1C" : "rgba(255,255,255,.72)" }}
                    >
                        {m.subject}
                    </span>
                )}
                <p className="text-[13.5px] leading-relaxed whitespace-pre-line break-words">{m.message}</p>

                <AttachmentList items={m.attachments} onAccent failed={failed} />
            </div>

            <span className="flex items-center gap-1.5 mt-1 me-1 text-[10.5px] font-semibold text-gray-400">
                {m.status === "sending" ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Sending</>
                ) : failed ? (
                    <>
                        <AlertCircle className="w-3 h-3 text-red-500" />
                        <span className="text-red-600">Not sent</span>
                        <button onClick={() => onRetry(m)} className="acc-text inline-flex items-center gap-1 font-bold hover:underline">
                            <RotateCcw className="w-3 h-3" /> Try again
                        </button>
                    </>
                ) : (
                    <>{fmtClock(m.sentAt)} <Check className="w-3 h-3" /> Sent</>
                )}
            </span>
        </div>
    );
};

/* ── Page ───────────────────────────────────────────────────────────────── */

export default function MessageAdmin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user: authUser, loading: authLoading, logout } = useAuth();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const threadRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLTextAreaElement>(null);

    // Reply context, when we arrived from a notification.
    const replyToId = searchParams.get("replyTo");
    const replyTitleParam = searchParams.get("replyTitle");
    const [replyTo, setReplyTo] = useState<string | null>(replyTitleParam);
    /** Whether we are still answering that notification. Starting a fresh
     *  conversation drops the context, so the intake runs and the message is not
     *  filed against the old notification. */
    const [replyCtx, setReplyCtx] = useState(!!replyToId);

    const [subject, setSubject] = useState(replyTitleParam ? `Re: ${replyTitleParam}` : "");
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [history, setHistory] = useState<ThreadMessage[]>([]);
    const [pending, setPending] = useState<ThreadMessage[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    /** Sticks after a successful send, so the delivery note does not blink out
     *  when the optimistic bubble is swapped for the stored one. */
    const [justSent, setJustSent] = useState(false);

    /* Conversations. The store is flat; `conversationId` groups it, and exactly
       one conversation is on screen at a time. */
    const [activeConv, setActiveConv] = useState("");
    const [convMenu, setConvMenu] = useState(false);
    const convMenuRef = useRef<HTMLDivElement>(null);
    /** Set once the person has switched or started a conversation themselves,
     *  after which nothing in the background may move them elsewhere. */
    const pickedConv = useRef(false);
    /** Set when the thread endpoint 404s, i.e. this page is running against a
     *  backend that predates it. Everything still works off the old read; there
     *  is just nothing to poll for, because that backend has no way to write a
     *  reply into the thread either. */
    const noThreadApi = useRef(false);

    /* The guided intake. `stage` is which question is live, `turns` the bubbles
       it has produced. Declared here because the thread-scroll effect below
       re-pins on them. */
    const [stage, setStage] = useState<"topic" | "narrow" | "open">("topic");
    const [turns, setTurns] = useState<Turn[]>([]);
    const [typing, setTyping] = useState(false);
    const [flowTopic, setFlowTopic] = useState<Topic | null>(null);
    const timers = useRef<number[]>([]);

    const firstName = (authUser?.firstName || authUser?.displayName || "").trim().split(" ")[0] || "there";

    /* The thread, both sides of it, oldest first. Its own endpoint rather than
       the whole user record, because this is polled every few seconds and that
       record carries notifications and their base64 attachments. */
    const loadHistory = useCallback(async (): Promise<boolean> => {
        if (!authUser?.uid) return false;
        try {
            const idToken = getAuthTokens()?.idToken;
            const headers: Record<string, string> = idToken ? { Authorization: `Bearer ${idToken}` } : {};

            let raw: any[] | null = null;
            if (!noThreadApi.current) {
                const resp = await fetch(`${API_BASE_URL}/auth/user-messages`, { headers });
                if (resp.ok) {
                    const json = await resp.json().catch(() => null);
                    if (json?.success) raw = Array.isArray(json.messages) ? json.messages : [];
                } else if (resp.status === 404) {
                    noThreadApi.current = true;
                }
            }

            /* Deploy skew: this page can ship before the backend that serves the
               endpoint above. Reading the thread out of the whole user record is
               the old, slower way — worth keeping so a mismatch shows a stale
               thread rather than an empty one. */
            if (!raw) {
                const legacy = await fetch(`${API_BASE_URL}/auth/user/${authUser.uid}`, { headers });
                if (!legacy.ok) return false;
                const json = await legacy.json().catch(() => null);
                const stored = (json?.success ? json.user : json)?.messages;
                raw = stored && typeof stored === "object"
                    ? Object.entries(stored).map(([id, v]: [string, any]) => ({ id, ...v }))
                    : [];
            }

            const list: ThreadMessage[] = raw.map((v: any) => ({
                id: v.id,
                from: v.from === "admin" ? "admin" : "client",
                conversationId: v.conversationId || LEGACY_CONV,
                subject: v.subject || "",
                message: v.message || "",
                attachments: Array.isArray(v.attachments) ? v.attachments : [],
                replyToTitle: v.replyToTitle || null,
                adminName: v.adminName || "",
                sentAt: v.sentAt || "",
            }));
            list.sort((a, b) => Date.parse(a.sentAt) - Date.parse(b.sentAt));
            setHistory(list);
            /* Retire optimistic copies the server has confirmed. A poll can land
               mid-send, and the same message twice reads worse than a late one. */
            setPending(prev => prev.filter(x =>
                x.status !== "sent" ||
                !list.some(m => m.from === "client" && m.conversationId === x.conversationId && m.message === x.message)
            ));
            return true;
        } catch (e) {
            console.error("Error loading message history:", e);
            return false;
        }
    }, [authUser?.uid]);

    useEffect(() => {
        if (authLoading) return;
        if (!authUser?.uid) { setLoadingHistory(false); return; }
        let live = true;
        loadHistory().finally(() => { if (live) setLoadingHistory(false); });
        return () => { live = false; };
    }, [authLoading, authUser?.uid, loadHistory]);

    /* Poll for replies while the tab is in front, and catch up the moment it
       comes back. Without this the answer sits in the store until a reload. */
    useEffect(() => {
        if (!authUser?.uid) return;
        const tick = () => {
            if (noThreadApi.current) return;
            if (document.visibilityState === "visible") loadHistory();
        };
        const iv = window.setInterval(tick, 25000);
        document.addEventListener("visibilitychange", tick);
        return () => { clearInterval(iv); document.removeEventListener("visibilitychange", tick); };
    }, [authUser?.uid, loadHistory]);

    /* Grouped, oldest conversation first. */
    const conversations = useMemo(() => {
        const byId = new Map<string, ThreadMessage[]>();
        for (const m of history) {
            const list = byId.get(m.conversationId);
            if (list) list.push(m); else byId.set(m.conversationId, [m]);
        }
        return [...byId.entries()]
            .map(([id, msgs]) => ({
                id,
                msgs,
                last: msgs[msgs.length - 1]?.sentAt || "",
                title: convTitle(msgs),
            }))
            .sort((a, b) => Date.parse(a.last) - Date.parse(b.last));
    }, [history]);

    /* Open on the most recent conversation, once, and never move them after
       that — a poll that reshuffled the pane mid-sentence would be worse than
       no polling at all. */
    useEffect(() => {
        if (loadingHistory || pickedConv.current || activeConv) return;
        const newest = conversations[conversations.length - 1];
        setActiveConv(newest ? newest.id : newConvId());
    }, [loadingHistory, conversations, activeConv]);

    /** Is the active conversation one that exists yet? */
    const isFreshConv = !!activeConv && !conversations.some(c => c.id === activeConv);

    /* Close the switcher on an outside click, the way a menu should. */
    useEffect(() => {
        if (!convMenu) return;
        const onDown = (e: MouseEvent) => {
            if (!convMenuRef.current?.contains(e.target as Node)) setConvMenu(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [convMenu]);

    /* The pane shows one conversation. */
    const thread = useMemo(
        () => [
            ...history.filter(m => m.conversationId === activeConv),
            ...pending.filter(m => m.conversationId === activeConv),
        ],
        [history, pending, activeConv],
    );

    /* Pin to the newest message, the way a chat pane does. */
    useEffect(() => {
        const el = threadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [thread.length, loadingHistory, attachments.length, error, replyTo, turns.length, typing, stage]);

    /* Composer grows with the text, up to a few lines. */
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
    }, [message]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        setError("");
        const added: Attachment[] = [];
        for (const file of files) {
            if (file.size > 10 * 1024 * 1024) {
                setError(`${file.name} is over the 10 MB limit.`);
                continue;
            }
            // Base64 inline — each message row carries its own attachments.
            const b64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            added.push({ name: file.name, url: b64, type: file.type, size: file.size });
        }
        setAttachments(prev => [...prev, ...added]);
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeAttachment = (i: number) => setAttachments(prev => prev.filter((_, idx) => idx !== i));

    /* A chat has no subject line, so the topic is derived from the first line
       unless the field or a chip set one. The team still gets something to scan. */
    const deriveTopic = (body: string) => {
        const typed = subject.trim();
        if (typed) return typed;
        const first = (body.split("\n")[0] || "").trim();
        if (!first) return "New message";
        return first.length > 72 ? `${first.slice(0, 69).trimEnd()}\u2026` : first;
    };

    const handleSend = async () => {
        const body = message.trim();
        if (!body || sending) return;
        if (!authUser?.uid) { setError("You need to be signed in to send a message."); return; }

        const conversationId = activeConv || newConvId();
        if (!activeConv) setActiveConv(conversationId);

        const tempId = `pending-${Date.now()}`;
        const optimistic: ThreadMessage = {
            id: tempId,
            from: "client",
            conversationId,
            subject: deriveTopic(body),
            message: body,
            attachments,
            replyToTitle: replyTo,
            sentAt: new Date().toISOString(),
            status: "sending",
        };
        setPending(prev => [...prev, optimistic]);
        setMessage("");
        setAttachments([]);
        setError("");
        setSending(true);

        try {
            const idToken = getAuthTokens()?.idToken;
            const resp = await fetch(`${API_BASE_URL}/auth/user-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                },
                body: JSON.stringify({
                    conversationId,
                    subject: optimistic.subject,
                    message: optimistic.message,
                    attachments: optimistic.attachments,
                    replyToId: replyCtx ? replyToId : null,
                    replyToTitle: replyTo || null,
                    senderName: `${authUser.displayName || ""}`.trim() || authUser.email || "",
                    senderEmail: authUser.email || "",
                }),
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok || !json.success) throw new Error(json.error || "Failed to send");

            setPending(prev => prev.map(m => (m.id === tempId ? { ...m, status: "sent" } : m)));
            setJustSent(true);
            setSubject("");
            // Once the server list has it, the optimistic copy can go.
            if (await loadHistory()) setPending(prev => prev.filter(m => m.id !== tempId));
        } catch (e: any) {
            setPending(prev => prev.map(m => (m.id === tempId ? { ...m, status: "failed" } : m)));
            setError(e?.message || "Could not send that message. Check your connection and try again.");
        } finally {
            setSending(false);
        }
    };

    /** Put a failed message back in the composer rather than losing the text. */
    const retry = (m: ThreadMessage) => {
        setPending(prev => prev.filter(x => x.id !== m.id));
        setSubject(m.subject);
        setMessage(m.message);
        setAttachments(m.attachments);
        setError("");
        boxRef.current?.focus();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !e.altKey && !(e.nativeEvent as any).isComposing) {
            e.preventDefault();
            handleSend();
        }
    };

    /* ── The guided exchange ─────────────────────────────────────────────────
       `stage` is where the assistant is: asking for a topic, narrowing it down,
       or done and out of the way. `turns` are the bubbles it has produced so
       far. Both are ephemeral — only the composed message is ever sent.

       The composer stays live at every stage on purpose. Someone who already
       knows what they want to say should never have to answer two questions
       first; typing simply retires the assistant. */
    /* Clear the pending "typing" timers if the page goes away mid-beat. */
    useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

    /* Skip the intake when the conversation on screen already has messages, or
       when we are answering a notification — both cases have their context, and
       being asked "what is this about?" over the top of it reads as the product
       not paying attention. A brand-new conversation gets asked. */
    useEffect(() => {
        if (loadingHistory || !activeConv) return;
        if (replyCtx || history.some(m => m.conversationId === activeConv)) setStage("open");
    }, [loadingHistory, history, activeConv, replyCtx]);

    /* Start another conversation. Nothing is stored until its first message is
       sent, so an abandoned one leaves no trace; the intake runs again because a
       new conversation genuinely has no context to carry over. */
    const startNewConversation = () => {
        pickedConv.current = true;
        setConvMenu(false);
        setActiveConv(newConvId());
        setTurns([]);
        setFlowTopic(null);
        setStage("topic");
        setSubject("");
        setMessage("");
        setAttachments([]);
        setError("");
        setJustSent(false);
        setReplyCtx(false);
        setReplyTo(null);
    };

    const openConversation = (id: string) => {
        pickedConv.current = true;
        setConvMenu(false);
        setActiveConv(id);
        setTurns([]);
        setFlowTopic(null);
        setStage("open");
        setSubject("");
        setError("");
        setJustSent(false);
    };

    /** Speaks after a beat, so the exchange has the rhythm of a reply rather
     *  than appearing fully formed. Honest about the wait: it IS instant, the
     *  pause is only there to make the turn readable. */
    const botSays = useCallback((text: string, then?: () => void) => {
        setTyping(true);
        timers.current.push(window.setTimeout(() => {
            setTyping(false);
            setTurns(prev => [...prev, { from: "bot", text }]);
            then?.();
        }, 620));
    }, []);

    const pickTopic = (t: Topic) => {
        setTurns(prev => [...prev, { from: "me", text: t.label }]);
        setFlowTopic(t);
        setSubject(t.label);
        if (t.ask && t.options?.length) {
            setStage("narrow");
            botSays(t.ask);
        } else {
            setStage("open");
            botSays(HANDOVER, () => boxRef.current?.focus());
        }
    };

    const pickDetail = (option: string) => {
        setTurns(prev => [...prev, { from: "me", text: option }]);
        /* Both picks go into the topic, so the team's queue shows the full path
           rather than just the broad category. */
        setSubject(flowTopic ? `${flowTopic.label} · ${option}` : option);
        setStage("open");
        botSays(HANDOVER, () => boxRef.current?.focus());
    };

    const canSend = !!message.trim() && !sending;


    return (
        <AppShell
            title={
                <span className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: ACCENT }}>
                        <MessageSquare className="w-5 h-5 text-white" />
                    </span>
                    Message Us
                </span>
            }
            subtitle={<span className="ms-11 block">Answer two quick questions and the team picks it up from there.</span>}
            crumb="Message Us"
            onLogout={logout}
        >
            {replyToId && (
                <button
                    onClick={() => navigate(`/notifications/${replyToId}`)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition mb-4"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to notification
                </button>
            )}

            <div className="mx-auto w-full" style={{ maxWidth: 880 }}>
                <div
                    className="flex flex-col bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden"
                    style={{ height: "min(74vh, 780px)", minHeight: 520 }}
                >
                    {/* ── Who you are writing to ── */}
                    <div className="flex items-center gap-3 px-4 md:px-5 py-3 border-b border-slate-200 bg-white shrink-0">
                        <span className="relative shrink-0">
                            <Avatar size={40} />
                            <span className="absolute -bottom-0.5 end-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-black text-gray-900 leading-tight">Conncct Team</p>
                            <p className="text-[11.5px] text-gray-500 font-medium truncate">
                                Assistant routes it · a person answers it
                            </p>
                        </div>
                        <span className="ms-auto flex items-center gap-1.5 shrink-0">
                            <span className="hidden lg:inline-flex items-center gap-1.5 rounded-full border acc-soft-bg acc-line px-3 py-1.5">
                                <Clock className="acc-ink w-3 h-3" />
                                <span className="acc-ink text-[10px] font-black tracking-wide">USUALLY WITHIN 1 WORKING DAY</span>
                            </span>

                            {conversations.length > 0 && (
                                <div className="relative" ref={convMenuRef}>
                                    <button
                                        onClick={() => setConvMenu(o => !o)}
                                        aria-expanded={convMenu}
                                        aria-haspopup="true"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-[11.5px] font-bold text-gray-600 hover:border-slate-300 transition"
                                        title="Your conversations"
                                    >
                                        <MessagesSquare className="w-3.5 h-3.5" />
                                        {conversations.length + (isFreshConv ? 1 : 0)}
                                        <ChevronDown className="w-3 h-3" />
                                    </button>

                                    {convMenu && (
                                        <div className="absolute end-0 top-full mt-1.5 w-64 z-30 rounded-2xl border border-slate-200 bg-white shadow-xl p-1.5">
                                            <p className="px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                                Your conversations
                                            </p>
                                            {[...conversations].reverse().map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => openConversation(c.id)}
                                                    className={`block w-full text-start rounded-xl px-2.5 py-2 transition hover:bg-slate-50 ${c.id === activeConv ? "bg-slate-50" : ""}`}
                                                >
                                                    <span className="block text-[12.5px] font-bold text-gray-800 truncate">{c.title}</span>
                                                    <span className="block text-[10.5px] text-gray-400 font-medium">
                                                        {dayLabel(c.last)} · {c.msgs.length} message{c.msgs.length === 1 ? "" : "s"}
                                                    </span>
                                                </button>
                                            ))}
                                            {isFreshConv && (
                                                <p className="px-2.5 py-1.5 text-[10.5px] font-medium text-gray-400">
                                                    Plus the new one you are writing.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={startNewConversation}
                                disabled={isFreshConv}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 text-[11.5px] font-bold text-gray-600 hover:border-slate-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                title={isFreshConv ? "This conversation is already new" : "Start another conversation"}
                            >
                                <Plus className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">New</span>
                            </button>
                        </span>
                    </div>

                    {/* ── Thread ── */}
                    <div
                        ref={threadRef}
                        role="log"
                        aria-live="polite"
                        aria-label="Your messages to the Conncct team"
                        className="mc-thread flex-1 overflow-y-auto px-3 md:px-5 py-4 space-y-2.5"
                        style={{ background: SHELL_BG }}
                    >
                        <SystemLine>
                            The assistant routes your message — a person answers it, and their reply appears
                            here as well as in Notifications and by email.
                        </SystemLine>

                        <TeamBubble withAvatar>
                            Hi {firstName}, you are through to the Conncct team.
                        </TeamBubble>
                        {stage === "open" && turns.length === 0 ? (
                            /* Returning, or replying to a notification: no interrogation. */
                            <TeamBubble withAvatar={false}>
                                Tell us what you need — a question about a filing, a document to look at, or a change to
                                your account. Attach anything that helps. Someone on the team reads every message and
                                answers in this conversation, usually within one working day.
                            </TeamBubble>
                        ) : (
                            <TeamBubble withAvatar={false}>What is this about?</TeamBubble>
                        )}

                        {/* The guided exchange, in the order it happened. */}
                        {turns.map((t, i) =>
                            t.from === "bot" ? (
                                <TeamBubble key={i} withAvatar={false}>{t.text}</TeamBubble>
                            ) : (
                                <div key={i} className="mc-b flex justify-end">
                                    <span
                                        className="rounded-2xl rounded-br-md px-3.5 py-2 text-[13.5px] font-semibold shadow-sm"
                                        style={{ background: ACCENT, color: "#fff" }}
                                    >
                                        {t.text}
                                    </span>
                                </div>
                            )
                        )}

                        {typing && (
                            <div className="mc-b flex items-end gap-2">
                                <span className="w-8 shrink-0" aria-hidden="true" />
                                <span className="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
                                    <span className="mc-dots" aria-hidden="true"><i /><i /><i /></span>
                                    <span className="sr-only">The assistant is typing</span>
                                </span>
                            </div>
                        )}

                        {/* Chips for whichever question is on the table. */}
                        {!loadingHistory && !typing && !sending && stage !== "open" && (
                            <div className="flex flex-wrap gap-2 ps-10 pt-1">
                                {(stage === "topic"
                                    ? TOPICS.map(t => ({ key: t.label, label: t.label, go: () => pickTopic(t) }))
                                    : (flowTopic?.options || []).map(o => ({ key: o, label: o, go: () => pickDetail(o) }))
                                ).map(c => (
                                    <button
                                        key={c.key}
                                        onClick={c.go}
                                        className="mc-chip rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-bold text-gray-600 hover:text-white transition shadow-sm"
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {loadingHistory ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                            </div>
                        ) : (
                            thread.map((m, i) => {
                                const prev = i > 0 ? thread[i - 1] : undefined;
                                const newDay = !prev || dayKey(prev.sentAt) !== dayKey(m.sentAt);
                                return (
                                    <Fragment key={m.id}>
                                        {newDay && m.sentAt && <DayChip iso={m.sentAt} />}
                                        {m.from === "admin"
                                            ? <AdminBubble m={m} />
                                            : <MyBubble m={m} onRetry={retry} />}
                                    </Fragment>
                                );
                            })
                        )}

                        {justSent && (
                            <SystemLine>Delivered to the team. Their reply arrives right here, and in Notifications.</SystemLine>
                        )}
                    </div>

                    {/* ── Composer ── */}
                    <div className="border-t border-slate-200 bg-white px-3 md:px-4 py-3 shrink-0">
                        {replyTo && (
                            <div className="flex items-center gap-2 rounded-xl acc-soft-bg acc-line border px-3 py-2 mb-2">
                                <CornerUpLeft className="acc-ink w-3.5 h-3.5 shrink-0" />
                                <p className="acc-ink text-[11.5px] font-semibold truncate flex-1">
                                    Replying to <span className="font-black">{replyTo}</span>
                                </p>
                                <button
                                    onClick={() => { setReplyTo(null); if (subject === `Re: ${replyTitleParam}`) setSubject(""); }}
                                    className="acc-ink hover:opacity-70 transition shrink-0"
                                    aria-label="Drop the reply context"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {attachments.map((att, i) => (
                                    <span
                                        key={i}
                                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 ps-2.5 pe-1.5 py-1.5 max-w-full"
                                    >
                                        <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                        <span className="text-[12px] font-semibold text-gray-800 truncate max-w-[180px]">{att.name}</span>
                                        <span className="text-[10.5px] text-gray-400 shrink-0">{fmtSize(att.size)}</span>
                                        <button
                                            onClick={() => removeAttachment(i)}
                                            className="text-gray-400 hover:text-red-500 transition shrink-0 p-0.5"
                                            aria-label={`Remove ${att.name}`}
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {error && (
                            <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-3 py-2 mb-2">
                                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                <p className="text-[12.5px] text-red-700 font-medium flex-1">{error}</p>
                                <button onClick={() => setError("")} className="text-red-400 hover:text-red-600 transition" aria-label="Dismiss">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 focus-within:bg-white focus-within:border-slate-300 transition overflow-hidden">
                            {/* Topic — optional, and only ever a hint for whoever picks this up. */}
                            <div className="flex items-center gap-2 px-3 pt-2 pb-1.5 border-b border-slate-200/70">
                                <Tag className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <label htmlFor="mc-topic" className="sr-only">Topic</label>
                                <input
                                    id="mc-topic"
                                    type="text"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="Topic (optional)"
                                    className="flex-1 min-w-0 bg-transparent text-[12.5px] font-bold text-gray-800 placeholder-gray-400 focus:outline-none"
                                />
                                {subject && (
                                    <button
                                        onClick={() => setSubject("")}
                                        className="text-gray-400 hover:text-gray-700 transition shrink-0"
                                        aria-label="Clear the topic"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>

                            <div className="flex items-end gap-1.5 px-2 py-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={handleFileChange}
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="w-9 h-9 rounded-full grid place-items-center text-gray-500 hover:bg-slate-200/70 transition shrink-0 disabled:opacity-50"
                                    aria-label="Attach a file"
                                    title="Attach a file"
                                >
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                                </button>

                                <label htmlFor="mc-message" className="sr-only">Message</label>
                                <textarea
                                    id="mc-message"
                                    ref={boxRef}
                                    rows={1}
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    placeholder="Write your message…"
                                    className="flex-1 min-w-0 bg-transparent resize-none py-2 text-[13.5px] leading-relaxed text-gray-800 placeholder-gray-400 focus:outline-none"
                                />

                                <button
                                    onClick={handleSend}
                                    disabled={!canSend}
                                    className="w-9 h-9 rounded-full grid place-items-center text-white shadow-sm transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                                    style={{ background: ACCENT }}
                                    aria-label="Send message"
                                    title="Send"
                                >
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <p className="text-[10.5px] text-gray-400 font-medium mt-1.5 px-1">
                            Enter to send · Shift + Enter for a new line · files up to 10 MB
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes mcIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
                .mc-b { animation: mcIn .2s ease-out both; }
                .mc-chip:hover { background: var(--app-acc); border-color: var(--app-acc); }
                .mc-thread::-webkit-scrollbar { width: 8px; }
                .mc-thread::-webkit-scrollbar-thumb { background: rgba(100,116,139,.28); border-radius: 8px; }
                .mc-thread::-webkit-scrollbar-track { background: transparent; }
                .mc-dots { display: flex; gap: 4px; align-items: center; }
                .mc-dots i {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: rgba(100,116,139,.5);
                    animation: mcDot 1.15s ease-in-out infinite;
                }
                .mc-dots i:nth-child(2) { animation-delay: .16s; }
                .mc-dots i:nth-child(3) { animation-delay: .32s; }
                @keyframes mcDot {
                    0%, 60%, 100% { transform: translateY(0); opacity: .45; }
                    30% { transform: translateY(-3px); opacity: 1; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .mc-b { animation: none; }
                    .mc-dots i { animation: none; opacity: .7; }
                }
            `}</style>
        </AppShell>
    );
}
